import { FunctionComponent, useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import DefaultHeader from 'components/UI/V3/DefaultHeader/DefaultHeader';
import { SelectionBar } from 'components/UI/V3/SelectionBar';
import SearchBar from 'components/UI/V3/SearchBar/SearchBar';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { useApiClientWrapper } from 'hooks';
import { Vehicle } from '@spike/vehicle-model';
import { fetchThunk, saveThunk, VehiclesStatus } from '@spike/vehicles-action';
import { showSuccess } from '@spike/notifications-action';
import { RootState } from 'store';
import useNonInitialEffect from '@versiondos/hooks';
import VehiclesTable from './List/VehiclesTable';
import VehiclesIsEmpty from './List/VehiclesIsEmpty';
import EditVehicle from './Form/EditVehicle';
import Spinner from 'components/UI/Spinner';
import styles from './TransportationVehicles.module.scss';

const TransportationVehicles: FunctionComponent = () => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const isMobile = useMediaQuery({ query: '(max-width:768px)' });

    // Redux selectors (vehicles is Array<Vehicle>, not PageResult)
    const vehicles = useSelector((state: RootState) => state.vehicles.vehicles);
    const vehiclesStatus = useSelector((state: RootState) => state.vehicles.status);

    // Local state
    const [loading, setLoading] = useState(true);
    const [editVehicle, setEditVehicle] = useState<{ show: boolean; vehicle: undefined | Vehicle }>({
        show: false,
        vehicle: undefined
    });
    const [selectedOption, setSelectedOption] = useState('active');
    const [searchText, setSearchText] = useState('');
    const [pendingAction, setPendingAction] = useState<'activate' | 'deactivate' | null>(null);

    // Fetch vehicles (no pagination - fetchThunk gets all vehicles)
    const fetchVehicles = useCallback(() => {
        setLoading(true);
        dispatch(fetchThunk(apiClientWrapper));
    }, []);

    // Initial fetch
    useEffect(() => {
        fetchVehicles();
    }, [fetchVehicles]);

    // Handle status changes (Pattern: Rooms)
    useNonInitialEffect(() => {
        switch (vehiclesStatus) {
            case VehiclesStatus.FetchSuccess:
            case VehiclesStatus.Error:
                setLoading(false);
                break;
            case VehiclesStatus.SaveSuccess:
                // Only refetch if modal is NOT open (activate/deactivate case)
                // If modal is open, refetch happens in savedHandler() after showing success screen
                if (!editVehicle.show) {
                    fetchVehicles();

                    // Show success notification based on action
                    if (pendingAction === 'activate') {
                        dispatch(showSuccess('Vehicle activated successfully'));
                    } else if (pendingAction === 'deactivate') {
                        dispatch(showSuccess('Vehicle deactivated successfully'));
                    }

                    // Clear pending action
                    setPendingAction(null);
                }
                break;
        }
    }, [vehiclesStatus]);

    // Filter vehicles by status and search (local filtering)
    const filteredVehicles = useMemo(() => {
        let filtered = vehicles;

        // Filter by status
        if (selectedOption === 'active') {
            filtered = filtered.filter(v => v.active);
        } else if (selectedOption === 'inactive') {
            filtered = filtered.filter(v => !v.active);
        }
        // 'all' shows everything

        // Filter by search text
        if (searchText) {
            filtered = filtered.filter(v => v.name.toLowerCase().includes(searchText.toLowerCase()));
        }

        return filtered;
    }, [vehicles, selectedOption, searchText]);

    // Status change handler
    const handleStatusChange = (statusId: string) => {
        setSelectedOption(statusId);
    };

    // Search change handler
    const handleSearchChange = (text: string) => {
        setSearchText(text);
    };

    // Activate handler
    const handleActivate = (vehicle: Vehicle) => {
        setPendingAction('activate');
        dispatch(
            saveThunk(apiClientWrapper, {
                ...vehicle,
                active: true
            })
        );
    };

    // Deactivate handler
    const handleDeactivate = (vehicle: Vehicle) => {
        setPendingAction('deactivate');
        dispatch(
            saveThunk(apiClientWrapper, {
                ...vehicle,
                active: false
            })
        );
    };

    // Edit handler
    const handleEdit = (vehicle: Vehicle) => {
        setEditVehicle({ show: true, vehicle });
    };

    // Saved handler
    const savedHandler = () => {
        setEditVehicle({ show: false, vehicle: undefined });
        fetchVehicles();
    };

    // Initial loading
    if (loading) {
        return (
            <div className={styles.loadingContainer}>
                <Spinner />
            </div>
        );
    }

    // Show empty state only when NO vehicles exist (not when filtered to 0)
    if (vehicles.length === 0 && !loading) {
        return (
            <>
                <div className={styles.root}>
                    <VehiclesIsEmpty onClick={() => setEditVehicle({ show: true, vehicle: undefined })} />
                </div>
                {editVehicle.show && (
                    <EditVehicle
                        vehicle={editVehicle.vehicle}
                        onClose={() => setEditVehicle({ show: false, vehicle: undefined })}
                        onSaved={savedHandler}
                    />
                )}
            </>
        );
    }

    // Has vehicles
    return (
        <>
            <div className={styles.root}>
                <div className={styles.headWrapper}>
                    <DefaultHeader
                        title={'Vehicles'}
                        buttonLabel={isMobile ? 'Add' : 'Add Vehicle'}
                        displayButton={true}
                        onClick={() => setEditVehicle({ show: true, vehicle: undefined })}
                        id="vehicles_add_button"
                        className={styles.headerContainer}
                        buttonIcon={faPlus}
                    />

                    <SelectionBar
                        options={[
                            { id: 'active', name: 'Active' },
                            { id: 'inactive', name: 'Inactive' },
                            { id: 'all', name: 'All' }
                        ]}
                        selectedId={selectedOption}
                        onSelect={handleStatusChange}
                        className={styles.selectionBar}
                    />

                    <SearchBar
                        searchText={searchText}
                        onChangeSearchText={handleSearchChange}
                        placeholder="Search by vehicle name"
                        className={styles.searchBarContainer}
                    />
                </div>

                <div className={styles.tableContainer}>
                    <VehiclesTable
                        vehicles={filteredVehicles}
                        isLoading={loading}
                        onEdit={handleEdit}
                        onActivate={handleActivate}
                        onDeactivate={handleDeactivate}
                    />
                </div>
            </div>
            {editVehicle.show && (
                <EditVehicle
                    vehicle={editVehicle.vehicle}
                    onClose={() => setEditVehicle({ show: false, vehicle: undefined })}
                    onSaved={savedHandler}
                />
            )}
        </>
    );
};

export default TransportationVehicles;
